import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPostAction,
  clearCreatePostFail,
} from "../../redux/actions/postActions";
import { getFileUploadSettings } from "../../redux/api/userAPI";
import InappropriatePostModal from "../modals/InappropriatePostModal";
import TopicConflictModal from "../modals/TopicConflictModal";
import EligibilityDetectionFailModal from "../modals/EligibilityDetectionFailModal";
import { useToast } from "../../contexts/ToastContext";
import { FaUserFriends, FaUsers, FaLock, FaVideo, FaGlobe } from "react-icons/fa";
import useMediaQuery from "../../hooks/useMediaQuery"
const PostForm = ({ communityId, communityName }) => {
  const isSmallScreen = useMediaQuery("(max-width: 912px)");
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();

  // Get joined communities from Redux
  const joinedCommunities = useSelector((state) => state.community?.joinedCommunities);

  const [showInappropriateContentModal, setShowInappropriateContentModal] =
    useState(false);
  const [showTopicConflictModal, setShowTopicConflictModal] = useState(false);
  const [
    showEligibilityDetectionFailModal,
    setShowEligibilityDetectionFailModal,
  ] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const [formData, setFormData] = useState({
    content: "",
    file: null,
    youtubeVideo: null,
    error: "",
    loading: false,
    audience: "Followers", // Default audience
    selectedCommunities: [], // Array of selected community IDs
    allowComments: true, // Default to allowing comments
  });

  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    url: "",
    isValid: false,
    videoData: null,
    loading: false,
    error: ""
  });

  const [fileUploadSettings, setFileUploadSettings] = useState({
    maxFileSizeMB: null,
    allowedFileTypes: []
  });

  const [textareaHeight, setTextareaHeight] = useState(70); // Initial height in pixels

  const { isPostInappropriate, postCategory, confirmationToken } = useSelector(
    (state) => ({
      isPostInappropriate: state.posts?.isPostInappropriate,
      postCategory: state.posts?.postCategory,
      confirmationToken: state.posts?.confirmationToken,
    })
  );

  // Initialize form with community if communityId is provided
  useEffect(() => {
    if (communityId) {
      setFormData(prev => ({
        ...prev,
        audience: "Communities",
        selectedCommunities: [communityId]
      }));
    }
  }, [communityId]);

  // Fetch file upload settings on component mount
  useEffect(() => {
    const fetchFileUploadSettings = async () => {
      try {
        const { error, data } = await getFileUploadSettings();
        if (!error && data) {
          setFileUploadSettings({
            maxFileSizeMB: data.maxFileSizeMB,
            allowedFileTypes: data.allowedFileTypes
          });
        } else {
          console.warn("File upload settings not configured in database");
          // Keep environment variable fallback if no database config
        }
      } catch (error) {
        console.error("Error fetching file upload settings:", error);
        // Keep environment variable fallback if API call fails
      }
    };

    fetchFileUploadSettings();
  }, []);

  const handleContentChange = (event) => {
    setFormData({
      ...formData,
      content: event.target.value,
    });

    // Auto-resize textarea
    const textarea = event.target;
    const maxHeight = window.innerHeight / 3; // One-third of screen height
    const minHeight = 40; // Minimum height

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);

    // Set the new height
    textarea.style.height = `${newHeight}px`;
    setTextareaHeight(newHeight);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Check if settings are loaded
    if (!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length) {
      showError("File upload settings are still loading. Please wait a moment and try again.");
      return;
    }

    const maxSizeBytes = fileUploadSettings.maxFileSizeMB * 1024 * 1024;

    // Check file size
    if (selectedFile.size > maxSizeBytes) {
      const errorMessage = `Please select a file under ${fileUploadSettings.maxFileSizeMB}MB.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // Check file type - only allow images
    if (!selectedFile.type.startsWith('image/')) {
      const errorMessage = `Please select an image file.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // File is valid
    setFormData({
      ...formData,
      file: selectedFile,
      error: "",
    });
  };

  // Video URL validation (YouTube only)
  const validateVideoUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  // Extract video ID from YouTube URL
  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Fetch video data (YouTube only)
  const fetchVideoData = async (videoId) => {
    try {
      // Using YouTube oEmbed API for thumbnail and basic info
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      if (!response.ok) throw new Error('Video not found');
      return await response.json();
    } catch (error) {
      throw new Error('Unable to fetch video information');
    }
  };

  // Handle video URL input
  const handleVideoUrlChange = async (url) => {
    setVideoModal(prev => ({ ...prev, url, error: "" }));

    if (!url.trim()) {
      setVideoModal(prev => ({ ...prev, isValid: false, videoData: null }));
      return;
    }

    if (!validateVideoUrl(url)) {
      setVideoModal(prev => ({
        ...prev,
        isValid: false,
        videoData: null,
        error: "Please enter a valid YouTube URL"
      }));
      return;
    }

    const videoId = extractVideoId(url);
    if (!videoId) {
      setVideoModal(prev => ({
        ...prev,
        isValid: false,
        videoData: null,
        error: "Unable to extract video ID from URL"
      }));
      return;
    }

    setVideoModal(prev => ({ ...prev, loading: true }));

    try {
      const videoData = await fetchVideoData(videoId);
      setVideoModal(prev => ({
        ...prev,
        isValid: true,
        videoData: { ...videoData, videoId },
        loading: false,
        error: ""
      }));
    } catch (error) {
      setVideoModal(prev => ({
        ...prev,
        isValid: false,
        videoData: null,
        loading: false,
        error: error.message
      }));
    }
  };

  // Add video to post
  const addVideo = () => {
    if (videoModal.isValid && videoModal.videoData) {
      setFormData(prev => ({
        ...prev,
        youtubeVideo: videoModal.videoData,
        error: ""
      }));
      setVideoModal({
        isOpen: false,
        url: "",
        isValid: false,
        videoData: null,
        loading: false,
        error: ""
      });
    }
  };

  // Remove video
  const removeVideo = () => {
    setFormData(prev => ({
      ...prev,
      youtubeVideo: null,
      error: ""
    }));
  };

  useEffect(() => {
    if (isPostInappropriate) setShowInappropriateContentModal(true);
    if (postCategory) setShowTopicConflictModal(true);
    if (confirmationToken) setShowEligibilityDetectionFailModal(true);
  }, [isPostInappropriate, postCategory, confirmationToken]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { content, file, youtubeVideo, loading, audience, selectedCommunities, allowComments } = formData;
    if (loading) return;

    if (!content.trim() && !file && !youtubeVideo) {
      const errorMessage = "Please enter a message, select a file, or add a video link.";
      setFormData({
        ...formData,
        error: errorMessage,
      });
      return;
    }

    // Validate community selection for "Communities" audience
    if (audience === "Communities" && (!selectedCommunities || selectedCommunities.length === 0)) {
      const errorMessage = "Please select at least one community.";
      showError(errorMessage);
      setFormData({
        ...formData,
        error: errorMessage,
      });
      return;
    }

    const newPost = new FormData();
    newPost.append("content", content);
    newPost.append("file", file);
    newPost.append("audience", audience);
    newPost.append("allowComments", allowComments);


    // Add YouTube video data if present
    if (youtubeVideo) {
      newPost.append("youtubeVideo", JSON.stringify(youtubeVideo));
    }

    // Handle different audience types
    if (audience === "Communities" && selectedCommunities.length > 0) {
      // Multiple communities post - send as JSON string
      newPost.append("selectedCommunities", JSON.stringify(selectedCommunities));
    } else if (communityId && communityName && audience !== "Communities") {
      // Legacy community-specific post (only if not using Communities audience)
      newPost.append("communityId", communityId);
      newPost.append("communityName", communityName);
    }
    // For "Private" and "Followers" audience, no community data is sent

    setFormData({
      ...formData,
      loading: true,
    });

    try {
      const result = await dispatch(createPostAction(newPost));

      if (result && result.success) {
        showSuccess("Post created successfully!");
        setFormData({
          content: "",
          file: null,
          youtubeVideo: null,
          error: "",
          loading: false,
          audience: "Followers",
          selectedCommunities: [],
        });
        setIsExpanded(false);
        event.target.reset();
      } else {
        setFormData({
          ...formData,
          loading: false,
        });
      }
    } catch (error) {
      setFormData({
        ...formData,
        loading: false,
      });
    }
  };

  const handleRemoveFile = () => {
    setFormData({
      ...formData,
      file: null,
      error: "",
    });
  };

  const handleCommunityToggle = (communityId) => {
    setFormData(prev => {
      const isSelected = prev.selectedCommunities.includes(communityId);
      const newSelectedCommunities = isSelected
        ? prev.selectedCommunities.filter(id => id !== communityId)
        : [...prev.selectedCommunities, communityId];


      return {
        ...prev,
        selectedCommunities: newSelectedCommunities
      };
    });
  };

  const handleAudienceChange = (audience) => {
    setFormData(prev => ({
      ...prev,
      audience,
      selectedCommunities: audience === "Communities" ? prev.selectedCommunities : []
    }));
  };

  return (
    <>
      <InappropriatePostModal
        closeInappropriateContentModal={() => {
          setShowInappropriateContentModal(false);
          dispatch(clearCreatePostFail());
        }}
        showInappropriateContentModal={showInappropriateContentModal}
        contentType={"post"}
      />

      <TopicConflictModal
        closeTopicConflictModal={() => {
          setShowTopicConflictModal(false);
          dispatch(clearCreatePostFail());
        }}
        showTopicConflictModal={showTopicConflictModal}
        communityName={postCategory?.community}
        recommendedCommunity={postCategory?.recommendedCommunity}
      />

      <EligibilityDetectionFailModal
        closeEligibilityDetectionFailModal={() => {
          setShowEligibilityDetectionFailModal(false);
          dispatch(clearCreatePostFail());
        }}
        showEligibilityDetectionFailModal={showEligibilityDetectionFailModal}
        confirmationToken={confirmationToken}
      />
      {!isSmallScreen ?
        <div className="bg-white rounded-lg border pt-4 pr-6 pb-3 pl-3 md:pt-5 md:pr-8 md:pb-4 md:pl-4 shadow-sm w-full">
          {!isExpanded ? (
            <div
              className="flex items-start gap-2 md:gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-0 transition-colors mb-1 md:mb-0 mt-0"
              onClick={() => setIsExpanded(true)}
            >
              <div className="flex-shrink-0">
                <img
                  src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                  alt="Profile"
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
              </div>
              <div className="flex-1 bg-gray-50 rounded-full px-4 py-3 border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center">
                <span className="text-gray-500 font-roboto-flex text-base">
                  What's on your mind?
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="flex items-start gap-2 md:gap-3 mb-1 md:mb-2">
                <div className="flex-shrink-0">
                  <img
                    src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                    alt="Profile"
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <textarea
                    className="w-full shadow-sm hover:shadow-md transition-shadow resize-none border border-gray-200 rounded-lg px-3 md:px-3 py-2 md:py-2 text-base md:text-lg placeholder-gray-500 text-gray-700 font-roboto-flex bg-gray-50 focus:bg-white focus:border-gray-300 transition-colors outline-none"
                    name="content"
                    id="content"
                    value={formData.content}
                    onChange={handleContentChange}
                    minLength={1}
                    maxLength={5000}
                    placeholder="What's on your mind?"
                    style={{ height: `${textareaHeight}px` }}
                    autoFocus
                  />
                  <div className="mt-1">
                    {(formData.file || formData.youtubeVideo) ? (
                      // File/Video Preview
                      <div className="space-y-2">
                        <div className="relative">
                          <div className="w-full aspect-[21/9] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                            {formData.file ? (
                              <img
                                src={URL.createObjectURL(formData.file)}
                                alt="Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : formData.youtubeVideo ? (
                              <div className="w-full h-full relative">
                                <img
                                  src={formData.youtubeVideo.thumbnail_url}
                                  alt={formData.youtubeVideo.title}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                  <FaVideo className="text-white text-4xl" />
                                </div>
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={formData.file ? handleRemoveFile : removeVideo}
                            className="absolute top-2 right-2 bg-action text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-action-hover transition-colors"
                          >
                            ×
                          </button>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                            <span className="text-xs text-gray-500 font-roboto-flex">
                              {formData.content.length}/5000
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {[
                              { name: "Followers", icon: FaUserFriends },
                              { name: "Communities", icon: FaUsers },
                              { name: "Anyone", icon: FaGlobe },
                              { name: "Private", icon: FaLock }
                            ].map(({ name, icon: Icon }) => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => handleAudienceChange(name)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${formData.audience === name
                                  ? "bg-primary-700 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                              >
                                <Icon className="w-3 h-3" />
                                {name}
                              </button>
                            ))}
                          </div>

                          {/* Comments Toggle */}
                          <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                              <span className="text-xs text-gray-500 font-roboto-flex">
                                {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${!formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                          </div>

                          {/* Communities Selection */}
                          {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                Select communities:
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {joinedCommunities.map((community) => (
                                  <button
                                    key={community._id}
                                    type="button"
                                    onClick={() => handleCommunityToggle(community._id)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${formData.selectedCommunities.includes(community._id)
                                      ? "bg-primary-700 text-white border-primary-700"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                      }`}
                                  >
                                    {community.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Add photo and YouTube video buttons
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <label
                              htmlFor="file"
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-green-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add photos</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setVideoModal(prev => ({ ...prev, isOpen: true }))}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                            >
                              <FaVideo className="h-4 w-4 text-action" />
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add video link</span>
                            </button>
                          </div>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            {formData.content.length}/5000
                          </span>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                          <div className="flex gap-2">
                            {[
                              { name: "Followers", icon: FaUserFriends },
                              { name: "Communities", icon: FaUsers },
                              { name: "Anyone", icon: FaGlobe },
                              { name: "Private", icon: FaLock }
                            ].map(({ name, icon: Icon }) => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => handleAudienceChange(name)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${formData.audience === name
                                  ? "bg-primary-700 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                              >
                                <Icon className="w-3 h-3" />
                                {name}
                              </button>
                            ))}
                          </div>

                          {/* Comments Toggle */}
                          <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                              <span className="text-xs text-gray-500 font-roboto-flex">
                                {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${!formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                          </div>

                          {/* Communities Selection */}
                          {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                Select communities:
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {joinedCommunities.map((community) => (
                                  <button
                                    key={community._id}
                                    type="button"
                                    onClick={() => handleCommunityToggle(community._id)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${formData.selectedCommunities.includes(community._id)
                                      ? "bg-primary-700 text-white border-primary-700"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                      }`}
                                  >
                                    {community.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <input
                name="file"
                type="file"
                id="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length}
                className="hidden"
              />

              <div className="border-t border-gray-200 mt-4 pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsExpanded(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 font-roboto-flex"
                    >
                      Cancel
                    </button>
                    {(formData.content.trim() || formData.file || formData.youtubeVideo) && (
                      <button
                        type="submit"
                        disabled={formData.loading}
                        className="px-6 py-2 rounded-lg text-sm font-medium transition-colors bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 font-roboto-flex"
                      >
                        {formData.loading ? "Posting..." : "Post"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}

        </div>
        :
        // adding responsive padding to make it tighter on mobile
        <div className="bg-white rounded-lg border shadow-sm w-full overflow-visible">
          {!isExpanded ? (
            <div
              className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-2 transition-colors"
              onClick={() => setIsExpanded(true)}
            >
              <div className="flex-shrink-0">
                <img
                  src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                  alt="Profile"
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
              </div>
              <div className="flex-1 bg-gray-50 rounded-full px-4 py-3 border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center">
                <span className="text-gray-500 font-roboto-flex text-base md:text-sm">
                  What's on your mind?
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div>
                <div className="flex items-center gap-3 mb-2 px-1 py-2">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <img
                      src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                      alt="Profile"
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                    />
                  </div>
                  <div className="w-full flex-1">
                    <textarea
                      className="flex-1 w-full shadow-sm hover:shadow-md transition-shadow resize-none border border-gray-200 rounded-lg 
               px-3 md:px-4 py-2 md:py-3 text-base md:text-lg placeholder-gray-500 text-gray-700 font-roboto-flex 
               bg-gray-50 focus:bg-white focus:border-gray-300 transition-colors outline-none"
                      name="content"
                      id="content"
                      value={formData.content}
                      onChange={handleContentChange}
                      minLength={1}
                      maxLength={5000}
                      placeholder="What's on your mind?"
                      style={{ height: `${textareaHeight}px` }}
                      autoFocus
                    />

                  </div>
                </div>
                <div className="w-full mt-2 space-y-2">
                  <div className="mt-1">
                    {(formData.file || formData.youtubeVideo) ? (
                      // File/Video Preview
                      <div className="space-y-2">
                        <div className="relative">
                          <div className="w-full aspect-[21/9] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                            {formData.file ? (
                              <img
                                src={URL.createObjectURL(formData.file)}
                                alt="Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : formData.youtubeVideo ? (
                              <div className="w-full h-full relative">
                                <img
                                  src={formData.youtubeVideo.thumbnail_url}
                                  alt={formData.youtubeVideo.title}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                  <FaVideo className="text-white text-4xl" />
                                </div>
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={formData.file ? handleRemoveFile : removeVideo}
                            className="absolute top-2 right-2 bg-action text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-action-hover transition-colors"
                          >
                            ×
                          </button>
                        </div>
                        <div className="space-y-1 px-3">
                          <div className="flex  items-center">
                            <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                            <span className="text-xs text-gray-500 font-roboto-flex">
                              {formData.content.length}/5000
                            </span>
                          </div>
                         <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 pt-1">
                            {[
                              { name: "Followers", icon: FaUserFriends },
                              { name: "Communities", icon: FaUsers },
                              { name: "Anyone", icon: FaGlobe },
                              { name: "Private", icon: FaLock }
                            ].map(({ name, icon: Icon }) => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => handleAudienceChange(name)}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors font-roboto-flex ${formData.audience === name
                                  ? "bg-primary-700 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                              >
                                <Icon className="w-3 h-3" />
                                {name}
                              </button>
                            ))}
                          </div>

                          {/* Comments Toggle */}
                          <div className="flex items-center justify-between mt-5 p-3 bg-gray-50 rounded-lg ">
                            <div className="flex items-center gap-2 ">
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                              {/* <span className="text-xs text-gray-500 font-roboto-flex">
                                {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                              </span> */}
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${!formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                          </div>

                          {/* Communities Selection */}
                          {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                Select communities:
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {joinedCommunities.map((community) => (
                                  <button
                                    key={community._id}
                                    type="button"
                                    onClick={() => handleCommunityToggle(community._id)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${formData.selectedCommunities.includes(community._id)
                                      ? "bg-primary-700 text-white border-primary-700"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                      }`}
                                  >
                                    {community.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Add photo and YouTube video buttons
                      <div className="space-y-2">
                        <div className="flex  items-center">
                          <div className="flex items-center gap-3">
                            <label
                              htmlFor="file"
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-green-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add photos</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setVideoModal(prev => ({ ...prev, isOpen: true }))}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                            >
                              <FaVideo className="h-4 w-4 text-action" />
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add video link</span>
                            </button>
                          </div>
                          <span className="text-xs text-gray-500 font-roboto-flex">
                            {formData.content.length}/5000
                          </span>
                        </div>
                     <div className="space-y-1 px-3">
                          <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                         <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 pt-1">
                            {[
                              { name: "Followers", icon: FaUserFriends },
                              { name: "Communities", icon: FaUsers },
                              { name: "Anyone", icon: FaGlobe },
                              { name: "Private", icon: FaLock }
                            ].map(({ name, icon: Icon }) => (
                              <button
                                key={name}
                                type="button"
                                onClick={() => handleAudienceChange(name)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${formData.audience === name
                                  ? "bg-primary-700 text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                              >
                                <Icon className="w-3 h-3" />
                                {name}
                              </button>
                            ))}
                          </div>

                          {/* Comments Toggle */}
                          <div className="flex items-center justify-between mt-5 p-3 bg-gray-50 rounded-lg ">
                            <div className="flex items-center gap-2 ">
                              <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                              {/* <span className="text-xs text-gray-500 font-roboto-flex">
                                {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                              </span> */}
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${!formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                          </div>

                          {/* Communities Selection */}
                          {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                Select communities:
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {joinedCommunities.map((community) => (
                                  <button
                                    key={community._id}
                                    type="button"
                                    onClick={() => handleCommunityToggle(community._id)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${formData.selectedCommunities.includes(community._id)
                                      ? "bg-primary-700 text-white border-primary-700"
                                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                      }`}
                                  >
                                    {community.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <input
                name="file"
                type="file"
                id="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length}
                className="hidden"
              />

              <div className="border-t border-gray-200 px-2 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsExpanded(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 font-roboto-flex"
                    >
                      Cancel
                    </button>
                    {(formData.content.trim() || formData.file || formData.youtubeVideo) && (
                      <button
                        type="submit"
                        disabled={formData.loading}
                        className="px-6 py-2 rounded-lg text-sm font-medium transition-colors bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 font-roboto-flex"
                      >
                        {formData.loading ? "Posting..." : "Post"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}

        </div>
      }


      {/* Video Link Modal */}
      {videoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 font-roboto-flex">Add Video Link</h3>
                <button
                  onClick={() => setVideoModal({
                    isOpen: false,
                    url: "",
                    isValid: false,
                    videoData: null,
                    loading: false,
                    error: ""
                  })}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="video-url" className="block text-sm font-medium text-gray-700 font-roboto-flex mb-2">
                    Video URL
                  </label>
                  <input
                    type="url"
                    id="video-url"
                    value={videoModal.url}
                    onChange={(e) => handleVideoUrlChange(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-roboto-flex"
                  />
                  {videoModal.error && (
                    <p className="mt-1 text-sm text-action font-roboto-flex">{videoModal.error}</p>
                  )}
                  <div className="mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <p className="text-xs text-gray-500 font-roboto-flex">
                      Please paste only YouTube video link.
                    </p>
                  </div>
                </div>

                {videoModal.loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-gray-600 font-roboto-flex">Loading video...</span>
                  </div>
                )}

                {videoModal.isValid && videoModal.videoData && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700 font-roboto-flex">Preview:</div>
                    <div className="relative">
                      <img
                        src={videoModal.videoData.thumbnail_url}
                        alt={videoModal.videoData.title}
                        className="w-full rounded-lg"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                        <FaVideo className="text-white text-3xl" />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 font-roboto-flex">
                      <div className="font-roboto-flex">{videoModal.videoData.title}</div>
                      <div className="text-xs text-gray-500 font-roboto-flex">by {videoModal.videoData.author_name}</div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setVideoModal({
                      isOpen: false,
                      url: "",
                      isValid: false,
                      videoData: null,
                      loading: false,
                      error: ""
                    })}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-roboto-flex"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addVideo}
                    disabled={!videoModal.isValid || videoModal.loading}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-roboto-flex"
                  >
                    Add Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostForm;
